import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface IEventSeriesEntityAttributes extends IModelAttributes {
	name: string;
	description: string;
	googleMapUrl: string;
	address: string;
	termsAndConditions: string;
	tags: string;
	isFeatured: boolean;

	customTicketTypes: Array<Models.CustomTicketTypeEntity | Models.ICustomTicketTypeEntityAttributes>;
	eventLabelId?: string;
	eventLabel?: Models.EventLabelEntity | Models.IEventLabelEntityAttributes;
	headerImage?: Models.UploadedImageEntity | Models.IUploadedImageEntityAttributes;
	supportingImages: Array<Models.UploadedImageEntity | Models.IUploadedImageEntityAttributes>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	itineraryPlan?: Models.ItineraryPlanEntity | Models.IItineraryPlanEntityAttributes;
	eventDetails?: Array<Models.EventDetailEntity | Models.IEventDetailEntityAttributes>;
}

@entity('EventSeriesEntity', 'Event Series')
export default class EventSeriesEntity extends Model implements IEventSeriesEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The name of the event series.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public name: string;

	/**
	 * The tags for the event series.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public tags: string;

	/**
	 * A description of the event series.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public description: string;

	/**
	 * The URL for the google map.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public googleMapUrl: string;

	/**
	 * The address of the starting location for the event.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public address: string;

	/**
	 * The terms and conditions for the event series.
	 */
	@observable
	@attribute<EventSeriesEntity, string>()
	public termsAndConditions: string;

	/**
	 * Whether or not an event series is a featured experience.
	 */
	@observable
	@attribute<EventSeriesEntity, boolean>()
	public isFeatured: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public customTicketTypes: Models.CustomTicketTypeEntity[] = [];

	@observable
	@attribute()
	public eventLabelId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventLabel: Models.EventLabelEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public headerImage?: Models.UploadedImageEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public supportingImages: Models.UploadedImageEntity[] = [];

	@observable
	@attribute()
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	/**
	 * The itinerary plan for this event series.
	 */
	@observable
	@attribute({ isReference: true })
	public itineraryPlan?: Models.ItineraryPlanEntity;

	/**
	 * The event detail occurrences for this series.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public eventDetails: Models.EventDetailEntity[] = [];

	constructor(attributes?: Partial<IEventSeriesEntityAttributes>) {
		super(attributes);

		this.customTicketTypes = [];
		this.supportingImages = [];
		this.eventDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IEventSeriesEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.googleMapUrl !== undefined) {
				this.googleMapUrl = attributes.googleMapUrl;
			}
			if (attributes.address !== undefined) {
				this.address = attributes.address;
			}
			if (attributes.termsAndConditions !== undefined) {
				this.termsAndConditions = attributes.termsAndConditions;
			}
			if (attributes.tags !== undefined) {
				this.tags = attributes.tags;
			}
			if (attributes.isFeatured !== undefined) {
				this.isFeatured = attributes.isFeatured;
			}
			if (attributes.customTicketTypes !== undefined && Array.isArray(attributes.customTicketTypes)) {
				if (!this.customTicketTypes) {
					this.customTicketTypes = [];
				}
				for (const model of attributes.customTicketTypes) {
					if (model instanceof Models.CustomTicketTypeEntity) {
						this.customTicketTypes.push(model);
					} else {
						this.customTicketTypes.push(new Models.CustomTicketTypeEntity(model));
					}
				}
			}
			if (attributes.eventLabelId !== undefined) {
				this.eventLabelId = attributes.eventLabelId;
			}
			if (attributes.eventLabel !== undefined) {
				if (attributes.eventLabel === null) {
					this.eventLabel = attributes.eventLabel;
				} else if (attributes.eventLabel instanceof Models.EventLabelEntity) {
					this.eventLabel = attributes.eventLabel;
					this.eventLabelId = attributes.eventLabel.id;
				} else {
					this.eventLabel = new Models.EventLabelEntity(attributes.eventLabel);
					this.eventLabelId = this.eventLabel.id;
				}
			}
			if (attributes.headerImage !== undefined) {
				if (attributes.headerImage === null) {
					this.headerImage = attributes.headerImage;
				} else if (attributes.headerImage instanceof Models.UploadedImageEntity) {
					this.headerImage = attributes.headerImage;
				} else {
					this.headerImage = new Models.UploadedImageEntity(attributes.headerImage);
				}
			}
			if (attributes.supportingImages !== undefined && Array.isArray(attributes.supportingImages)) {
				if (!this.supportingImages) {
					this.supportingImages = [];
				}
				for (const model of attributes.supportingImages) {
					if (model instanceof Models.UploadedImageEntity) {
						this.supportingImages.push(model);
					} else {
						this.supportingImages.push(new Models.UploadedImageEntity(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.organisationId)) {
				this.organisationId = attributes.organisationId;
			}
			if (isNotNullOrUndefined(attributes.organisation)) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}
			if (attributes.itineraryPlan !== undefined) {
				if (attributes.itineraryPlan === null) {
					this.itineraryPlan = attributes.itineraryPlan;
				} else if (attributes.itineraryPlan instanceof Models.ItineraryPlanEntity) {
					this.itineraryPlan = attributes.itineraryPlan;
				} else {
					this.itineraryPlan = new Models.ItineraryPlanEntity(attributes.itineraryPlan);
				}
			}
			if (attributes.eventDetails !== undefined && Array.isArray(attributes.eventDetails)) {
				if (!this.eventDetails) {
					this.eventDetails = [];
				}
				for (const model of attributes.eventDetails) {
					if (model instanceof Models.EventDetailEntity) {
						this.eventDetails.push(model);
					} else {
						this.eventDetails.push(new Models.EventDetailEntity(model));
					}
				}
			}
		}
	}
}
